import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { listInvoices } from '@/api/salesInvoice';
import { createGatePass, listGatePasses } from '@/api/gatePass';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateGatePassModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'invoiceId' | 'expectedReturn'>();
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [sealLockNo, setSealLockNo] = useState('');
  const [gateOfficer, setGateOfficer] = useState('');
  const [isReturnable, setIsReturnable] = useState(false);
  const [expectedReturnDate, setExpectedReturnDate] = useState('');

  const invQuery = useQuery({
    queryKey: ['sales-invoices', 'gp-eligible'],
    queryFn: () => listInvoices({ status: 'Issued' }),
    enabled: open,
  });
  const gpQuery = useQuery({
    queryKey: ['gate-passes', 'all-for-dedup'],
    queryFn: () => listGatePasses({}),
    enabled: open,
  });
  const eligible = useMemo(() => {
    const usedInvoiceIds = new Set(
      (gpQuery.data ?? [])
        .filter((g) => g.invoiceId !== null)
        .map((g) => g.invoiceId as number),
    );
    return (invQuery.data ?? []).filter(
      (inv) => !usedInvoiceIds.has(inv.id) && inv.status !== 'Cancelled',
    );
  }, [invQuery.data, gpQuery.data]);

  useEffect(() => {
    if (open) {
      setInvoiceId(null);
      setSealLockNo('');
      setGateOfficer('');
      setIsReturnable(false);
      setExpectedReturnDate('');
    }
  }, [open]);

  const createMut = useMutation({
    mutationFn: createGatePass,
    onSuccess: (r) => {
      toast.pushStep(`Gate pass ${r.gpNo} raised`, {
        body: 'Next step — mark as delivered once the vehicle leaves.',
        href: '/sales/gate-pass',
        nextLabel: 'Open Gate Pass list',
        tag: `gp-created-${r.id}`,
      });
      qc.invalidateQueries({ queryKey: ['gate-passes'] });
      onClose();
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  function submit() {
    if (!fe.validate({
      invoiceId: !invoiceId && 'Select an invoice first',
      expectedReturn: isReturnable && !expectedReturnDate && 'Expected return date required for returnable gate pass',
    })) return;
    createMut.mutate({
      invoiceId: invoiceId!,
      sealLockNo: sealLockNo || undefined,
      gateOfficer: gateOfficer || undefined,
      isReturnable,
      expectedReturnDate: expectedReturnDate || undefined,
    });
  }

  return (
    <Modal
      open={open}
      title="Raise Gate Pass"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={createMut.isPending || !invoiceId}
          >
            {createMut.isPending ? 'Issuing…' : 'Issue Gate Pass'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        <FormGroup label="Invoice (issued + un-passed)" required error={fe.errors.invoiceId}>
          <Select
            invalid={fe.has('invoiceId')}
            value={invoiceId ?? ''}
            onChange={(e) => { setInvoiceId(e.target.value ? Number(e.target.value) : null); fe.clear('invoiceId'); }}
          >
            <option value="">— Select Invoice —</option>
            {eligible.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.invoiceNo} · {inv.party?.name ?? '—'} · ₹{inv.grandTotal}
              </option>
            ))}
          </Select>
        </FormGroup>

        <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 sm:gap-3">
          <FormGroup label="Seal / Lock #">
            <Input value={sealLockNo} onChange={(e) => setSealLockNo(e.target.value)} />
          </FormGroup>
          <FormGroup label="Gate Officer">
            <Input value={gateOfficer} onChange={(e) => setGateOfficer(e.target.value)} />
          </FormGroup>
        </div>

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={isReturnable}
            onChange={(e) => setIsReturnable(e.target.checked)}
          />
          Returnable Gate Pass (sample / demo material)
        </label>
        {isReturnable && (
          <FormGroup label="Expected Return Date" required error={fe.errors.expectedReturn}>
            <Input
              type="date"
              invalid={fe.has('expectedReturn')}
              value={expectedReturnDate}
              onChange={(e) => { setExpectedReturnDate(e.target.value); fe.clear('expectedReturn'); }}
            />
          </FormGroup>
        )}

        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[10px] text-amber-900 sm:text-[11px]">
          Vehicle / driver / LR / E-way auto-fill from the linked invoice. Marking
          delivered later closes the enquiry pipeline.
        </div>
      </div>
    </Modal>
  );
}
